import React, { useMemo, useState } from 'react';
import { StyleSheet, Pressable, View, Platform, Linking, useWindowDimensions } from 'react-native';
import Svg, { Defs, LinearGradient, Stop, Path, Line, Text as SvgText } from 'react-native-svg';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';

const WINDOWS = ['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y', 'MAX'];
const CHART_HEIGHT = 180;
const CHART_PAD = { top: 12, right: 8, bottom: 28, left: 44 };

const createStyles = (colors) => StyleSheet.create({
  container: {
    marginTop: 12,
    marginBottom: 4,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    gap: 12,
  },
  card: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 8,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerLeft: {
    flex: 1,
    minWidth: 0,
  },
  windowRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 2,
    maxWidth: 220,
  },
  windowPill: {
    paddingHorizontal: 7,
    paddingVertical: 4,
    borderRadius: 999,
  },
  windowPillActive: {
    backgroundColor: colors.solid_color_tertiary || colors.solid_color_primary,
  },
  priceBlock: {
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  chartWrap: {
    width: '100%',
    height: CHART_HEIGHT,
  },
  statsGrid: {
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  statCell: {
    width: '33.33%',
    paddingVertical: 6,
    paddingRight: 8,
  },
});

function formatMove(value, movement, { percent = false, digits = 2 } = {}) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const abs = Math.abs(value).toFixed(digits);
  const down = movement === 'Down' || value < 0;
  const up = movement === 'Up' || value > 0;
  const sign = down ? '-' : up ? '+' : '';
  return percent ? `${sign}${abs}%` : `${sign}${abs}`;
}

function shortAsOf(asOf) {
  if (!asOf || typeof asOf !== 'string') return null;
  // "Jul 31 2026, 08:30:00 PM UTC-04:00" → "July 31"
  const m = asOf.match(/^([A-Za-z]{3})\s+(\d{1,2})/);
  if (!m) return asOf.split(',')[0] || null;
  const months = {
    Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
    May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
    Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
  };
  return `${months[m[1]] || m[1]} ${m[2]}`;
}

function formatAxisTime(dateStr, window) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.replace(' UTC', ' ').replace(/UTC([+-]\d{2}:\d{2})/, '$1'));
    if (Number.isNaN(d.getTime())) {
      // Fallback: parse "Jul 31 2026, 10:00 AM"
      const ampm = dateStr.match(/(\d{1,2}:\d{2}\s*[AP]M)/i);
      if (window === '1D' && ampm) return ampm[1].replace(/\s+/g, ' ');
      const day = dateStr.match(/^([A-Za-z]{3}\s+\d{1,2})/);
      return day ? day[1] : '';
    }
    if (window === '1D' || window === '5D') {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (_) {
    return '';
  }
}

function buildChartPaths(points, width, height, pad) {
  if (!points?.length || width <= 0) return null;
  const prices = points.map(p => p.price).filter(n => typeof n === 'number');
  if (prices.length < 2) return null;

  let min = Math.min(...prices);
  let max = Math.max(...prices);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const range = max - min;
  // Pad y range slightly
  min -= range * 0.05;
  max += range * 0.05;
  const yRange = max - min;

  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const xAt = (i) => pad.left + (i / (points.length - 1)) * plotW;
  const yAt = (price) => pad.top + plotH - ((price - min) / yRange) * plotH;

  let line = '';
  points.forEach((p, i) => {
    const x = xAt(i);
    const y = yAt(p.price);
    line += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  });

  const lastX = xAt(points.length - 1);
  const firstX = xAt(0);
  const baseY = pad.top + plotH;
  const area = `${line} L ${lastX} ${baseY} L ${firstX} ${baseY} Z`;

  // Y ticks (4)
  const yTicks = [0, 1, 2, 3].map(i => {
    const value = min + (yRange * i) / 3;
    return { value, y: yAt(value) };
  });

  // X ticks (~5)
  const xCount = Math.min(5, points.length);
  const xTicks = [];
  for (let i = 0; i < xCount; i++) {
    const idx = Math.round((i / (xCount - 1 || 1)) * (points.length - 1));
    xTicks.push({ index: idx, x: xAt(idx), date: points[idx]?.date });
  }

  return { line, area, yTicks, xTicks, min, max };
}

const StockChart = React.memo(function StockChart({
  points,
  width,
  strokeColor,
  gridColor,
  labelColor,
  window,
  gradientId,
}) {
  const paths = useMemo(
    () => buildChartPaths(points, width, CHART_HEIGHT, CHART_PAD),
    [points, width]
  );
  if (!paths || width < 80) {
    return <View style={{ height: CHART_HEIGHT }} />;
  }

  return (
    <Svg width={width} height={CHART_HEIGHT}>
      <Defs>
        <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <Stop offset="0" stopColor={strokeColor} stopOpacity="0.28" />
          <Stop offset="1" stopColor={strokeColor} stopOpacity="0.02" />
        </LinearGradient>
      </Defs>
      {paths.yTicks.map((tick, i) => (
        <React.Fragment key={`yt-${i}`}>
          <Line
            x1={CHART_PAD.left}
            x2={width - CHART_PAD.right}
            y1={tick.y}
            y2={tick.y}
            stroke={gridColor}
            strokeWidth={1}
          />
          <SvgText
            x={CHART_PAD.left - 6}
            y={tick.y + 4}
            fill={labelColor}
            fontSize="10"
            textAnchor="end"
          >
            {tick.value.toFixed(tick.value >= 100 ? 0 : 2)}
          </SvgText>
        </React.Fragment>
      ))}
      <Path d={paths.area} fill={`url(#${gradientId})`} />
      <Path d={paths.line} stroke={strokeColor} strokeWidth={2} fill="none" />
      {paths.xTicks.map((tick, i) => (
        <SvgText
          key={`xt-${i}`}
          x={tick.x}
          y={CHART_HEIGHT - 8}
          fill={labelColor}
          fontSize="10"
          textAnchor="middle"
        >
          {formatAxisTime(tick.date, window)}
        </SvgText>
      ))}
    </Svg>
  );
});

const StockQuoteCard = React.memo(function StockQuoteCard({ quote, colors, styles }) {
  const { width: screenWidth } = useWindowDimensions();
  const [activeWindow, setActiveWindow] = useState(quote.window || '1D');
  const [cardWidth, setCardWidth] = useState(Math.min(screenWidth - 48, 640));

  const isDown = quote.movement === 'Down' || (typeof quote.change === 'number' && quote.change < 0);
  const isUp = quote.movement === 'Up' || (typeof quote.change === 'number' && quote.change > 0);
  const trendColor = isDown
    ? (colors.color_destructive || '#FF3B30')
    : isUp
      ? (colors.color_success || '#22C55E')
      : (colors.text_color_secondary || '#888');

  const availableWindows = useMemo(() => {
    const map = quote.graphByWindow || {};
    return WINDOWS.filter(w => Array.isArray(map[w]) && map[w].length >= 2);
  }, [quote.graphByWindow]);

  const points = useMemo(() => {
    const map = quote.graphByWindow || {};
    const win = availableWindows.includes(activeWindow) ? activeWindow : (availableWindows[0] || '1D');
    return map[win] || [];
  }, [quote.graphByWindow, activeWindow, availableWindows]);

  const effectiveWindow = availableWindows.includes(activeWindow)
    ? activeWindow
    : (availableWindows[0] || '1D');

  const changeAbs = formatMove(quote.change, quote.movement);
  const changePct = formatMove(quote.changePercent, quote.movement, { percent: true });
  const asOfLabel = shortAsOf(quote.asOf);
  const title = quote.name
    ? `${quote.name}${quote.symbol ? ` (${quote.symbol})` : ''}`
    : (quote.symbol || 'Quote');

  const stats = Array.isArray(quote.keyStats) ? quote.keyStats.slice(0, 9) : [];
  const gradientId = `stockGrad-${quote._id || quote.symbol || 'x'}`;

  const openLink = () => {
    if (quote.link) Linking.openURL(quote.link).catch(() => {});
  };

  return (
    <View
      style={styles.card}
      onLayout={(e) => {
        const w = e.nativeEvent?.layout?.width;
        if (typeof w === 'number' && w > 0 && Math.abs(w - cardWidth) > 1) setCardWidth(w);
      }}
      accessibilityLabel={`${title}, ${quote.price || ''}`}
    >
      <View style={styles.header}>
        <Pressable
          style={styles.headerLeft}
          onPress={openLink}
          accessibilityRole="link"
          accessibilityLabel={`Open ${title} on Google Finance`}
        >
          <Text size="m" color="primary" numberOfLines={1} style={{ fontWeight: '600' }}>
            {title}
          </Text>
        </Pressable>
        {availableWindows.length > 0 && (
          <View style={styles.windowRow}>
            {availableWindows.map((w) => {
              const active = w === effectiveWindow;
              return (
                <Pressable
                  key={w}
                  onPress={() => setActiveWindow(w)}
                  style={[styles.windowPill, active && styles.windowPillActive]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  accessibilityLabel={`Chart range ${w}`}
                >
                  <Text
                    size="xs"
                    style={{
                      fontWeight: active ? '700' : '500',
                      color: active ? colors.text_color_primary : colors.text_color_secondary,
                      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
                    }}
                  >
                    {w}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        )}
      </View>

      <View style={styles.priceBlock}>
        {quote.price ? (
          <Text size="xxl" color="primary" style={{ fontWeight: '700', fontVariant: ['tabular-nums'] }}>
            {quote.price}
          </Text>
        ) : null}
        {(changeAbs || changePct) && (
          <Text size="s" style={{ color: trendColor, marginTop: 2, fontWeight: '600' }}>
            {[changeAbs, changePct ? `(${changePct})` : null, asOfLabel].filter(Boolean).join(' ')}
          </Text>
        )}
        {quote.extendedPrice && (
          <Text size="xs" color="secondary" style={{ marginTop: 2 }}>
            {[
              quote.extendedPrice,
              formatMove(quote.extendedChange, quote.extendedMovement),
              formatMove(quote.extendedChangePercent, quote.extendedMovement, { percent: true })
                ? `(${formatMove(quote.extendedChangePercent, quote.extendedMovement, { percent: true })})`
                : null,
              quote.extendedLabel,
            ].filter(Boolean).join(' ')}
          </Text>
        )}
      </View>

      <View style={styles.chartWrap}>
        <StockChart
          points={points}
          width={cardWidth}
          strokeColor={trendColor}
          gridColor={colors.border_color_secondary || '#333'}
          labelColor={colors.text_color_tertiary || colors.text_color_secondary || '#888'}
          window={effectiveWindow}
          gradientId={gradientId}
        />
      </View>

      {stats.length > 0 && (
        <View style={styles.statsGrid}>
          {stats.map((stat, i) => (
            <View key={`${stat.label}-${i}`} style={styles.statCell}>
              <Text size="xs" color="secondary" numberOfLines={1}>{stat.label}</Text>
              <Text size="s" color="primary" numberOfLines={1} style={{ fontWeight: '600', marginTop: 2 }}>
                {stat.value}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
});

function MessageStockQuotes({ stockQuotes = [] }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  if (!Array.isArray(stockQuotes) || stockQuotes.length === 0) return null;

  return (
    <View
      style={[
        styles.container,
        // Prevent Wide/Slim shrink-to-fit from blowing out the chat column
        Platform.OS === 'web' ? { width: 0, minWidth: '100%' } : null,
      ]}
    >
      {stockQuotes.map((quote, index) => (
        <StockQuoteCard
          key={quote._id || quote.symbol || index}
          quote={quote}
          colors={colors}
          styles={styles}
        />
      ))}
    </View>
  );
}

MessageStockQuotes.propTypes = {
  stockQuotes: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    symbol: PropTypes.string,
    exchange: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.string,
    extractedPrice: PropTypes.number,
    change: PropTypes.number,
    changePercent: PropTypes.number,
    movement: PropTypes.string,
    asOf: PropTypes.string,
    extendedPrice: PropTypes.string,
    extendedChange: PropTypes.number,
    extendedChangePercent: PropTypes.number,
    extendedLabel: PropTypes.string,
    window: PropTypes.string,
    graphByWindow: PropTypes.object,
    keyStats: PropTypes.array,
    link: PropTypes.string,
  })),
};

MessageStockQuotes.displayName = 'MessageStockQuotes';

export default React.memo(MessageStockQuotes);
